import React, { useState, useMemo } from 'react';
import { Student, Teacher, SchoolSettings, StudentExtracurricular, ExtracurricularGrade } from '../../types.ts';
import * as XLSX from 'xlsx';
import {
  Award,
  Plus,
  Search,
  Trash2,
  Edit3,
  Check,
  X,
  Printer,
  Download,
  Sparkles,
  Filter,
  Users,
  CheckCircle2,
  BookOpen,
  Info,
  Layers,
  FileText,
  UserCheck,
  ChevronDown,
  ArrowUpDown,
  Zap,
  HelpCircle
} from 'lucide-react';

interface ExtracurricularManagerProps {
  homeroomClass: string;
  students: Student[];
  activeUser: Teacher;
  schoolSettings: SchoolSettings;
  extracurriculars?: StudentExtracurricular[];
  onSaveExtracurricular?: (item: StudentExtracurricular, method: 'POST' | 'PUT' | 'DELETE') => Promise<any>;
  appTheme?: string;
}

const PRESET_EXTRAS = [
  'Pramuka (Wajib)',
  'Paskibra (Pasukan Pengibar Bendera)',
  'PMR (Palang Merah Remaja)',
  'PKS (Patroli Keamanan Sekolah)',
  'Seni Musik / Drumband',
  'Seni Tari & Budaya',
  'Olahraga - Futsal / Sepak Bola',
  'Olahraga - Bola Voli',
  'Olahraga - Bulu Tangkis',
  'Olahraga - Pencak Silat / Bela Diri',
  'English Club',
  'Rohis / Keagamaan Islam',
  'KIR (Karya Ilmiah Remaja)',
  'TIK & Komputer / Robotik',
  'Jurnalistik & Fotografi'
];

const PRESET_DESCRIPTIONS: Record<string, string[]> = {
  'Sangat Baik': [
    'Sangat aktif mengikuti seluruh kegiatan ekstrakurikuler, menunjukkan kedisiplinan, tanggung jawab, dan kemampuan kepemimpinan yang sangat baik.',
    'Menunjukkan antusiasme tinggi, menguasai keterampilan utama dengan sangat baik, dan aktif berkontribusi dalam setiap latihan serta kegiatan.',
    'Sangat disiplin, berdedikasi tinggi, serta mampu membimbing teman-temannya dengan penuh keteladanan.'
  ],
  'Baik': [
    'Aktif dan rajin mengikuti kegiatan ekstrakurikuler serta menunjukkan perkembangan keterampilan dan sikap kerja sama yang baik.',
    'Mengikuti latihan rutin dengan baik, bertanggung jawab menjalankan tugas, dan mampu bekerja sama dalam tim.',
    'Menunjukkan minat dan partisipasi aktif dalam setiap program kegiatan ekstrakurikuler dengan hasil yang baik.'
  ],
  'Cukup': [
    'Cukup aktif mengikuti kegiatan ekstrakurikuler, perlu meningkatkan kedisiplinan dan konsistensi kehadiran dalam setiap latihan.',
    'Mengikuti kegiatan dengan cukup baik, diharapkan dapat lebih berpartisipasi aktif pada kegiatan semester berikutnya.'
  ],
  'Kurang': [
    'Kurang aktif mengikuti kegiatan ekstrakurikuler, memerlukan motivasi dan pendampingan lebih lanjut untuk meningkatkan keikutsertaan.'
  ]
};

export const ExtracurricularManager: React.FC<ExtracurricularManagerProps> = ({
  homeroomClass,
  students = [],
  activeUser,
  schoolSettings,
  extracurriculars = [],
  onSaveExtracurricular,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [extraFilter, setExtraFilter] = useState('SEMUA');
  const [gradeFilter, setGradeFilter] = useState('SEMUA');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [semester, setSemester] = useState<string>(schoolSettings?.semester || 'Ganjil');
  const [academicYear, setAcademicYear] = useState<string>(schoolSettings?.academicYear || '2025/2026');

  // Sorted students list for dropdowns and consistency (Alphabetical A-Z)
  const sortedStudents = useMemo(() => {
    return [...students].sort((a, b) => 
      (a.name || '').localeCompare(b.name || '', 'id', { sensitivity: 'base' })
    );
  }, [students]);

  // Single Item Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<StudentExtracurricular | null>(null);

  // Form Fields
  const [formStudentId, setFormStudentId] = useState('');
  const [formExtraName, setFormExtraName] = useState(PRESET_EXTRAS[0]);
  const [formCustomExtraName, setFormCustomExtraName] = useState('');
  const [formGrade, setFormGrade] = useState<ExtracurricularGrade>('Sangat Baik');
  const [formDescription, setFormDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Selection State for Bulk Delete
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Filter Extracurricular records for current homeroom class
  const classExtracurriculars = useMemo(() => {
    return extracurriculars.filter(e => e.class === homeroomClass);
  }, [extracurriculars, homeroomClass]);

  // Available unique extra names for filter dropdown
  const uniqueExtraNames = useMemo(() => {
    const set = new Set<string>();
    classExtracurriculars.forEach(e => {
      if (e.extraName) set.add(e.extraName);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'id', { sensitivity: 'base' }));
  }, [classExtracurriculars]);

  // Filtered List - Default sorted by student Name alphabetically (A-Z)
  const filteredList = useMemo(() => {
    return classExtracurriculars
      .filter((e) => {
        const q = searchQuery.toLowerCase().trim();
        const matchSearch = !q || e.studentName.toLowerCase().includes(q) || e.extraName.toLowerCase().includes(q) || (e.description && e.description.toLowerCase().includes(q));
        const matchExtra = extraFilter === 'SEMUA' || e.extraName === extraFilter;
        const matchGrade = gradeFilter === 'SEMUA' || e.grade === gradeFilter || (gradeFilter === 'A' && (e.grade === 'A' || e.grade === 'Sangat Baik')) || (gradeFilter === 'B' && (e.grade === 'B' || e.grade === 'Baik'));
        return matchSearch && matchExtra && matchGrade;
      })
      .sort((a, b) => {
        const comp = (a.studentName || '').localeCompare(b.studentName || '', 'id', { sensitivity: 'base' });
        if (comp !== 0) {
          return sortDirection === 'asc' ? comp : -comp;
        }
        return (a.extraName || '').localeCompare(b.extraName || '', 'id', { sensitivity: 'base' });
      });
  }, [classExtracurriculars, searchQuery, extraFilter, gradeFilter, sortDirection]);

  // Group filtered list by student ID - Default sorted by student Name alphabetically (A-Z)
  const groupedList = useMemo(() => {
    const map = new Map<string, {
      studentId: string;
      studentName: string;
      items: StudentExtracurricular[];
    }>();

    filteredList.forEach((item) => {
      if (!map.has(item.studentId)) {
        map.set(item.studentId, {
          studentId: item.studentId,
          studentName: item.studentName,
          items: [],
        });
      }
      map.get(item.studentId)!.items.push(item);
    });

    return Array.from(map.values()).sort((a, b) => {
      const comp = (a.studentName || '').localeCompare(b.studentName || '', 'id', { sensitivity: 'base' });
      return sortDirection === 'asc' ? comp : -comp;
    });
  }, [filteredList, sortDirection]);

  // Check if all items in current filtered view are selected
  const isAllFilteredSelected = useMemo(() => {
    return filteredList.length > 0 && filteredList.every((item) => selectedIds.has(item.id));
  }, [filteredList, selectedIds]);

  // Toggle selection for all filtered items
  const handleToggleSelectAll = () => {
    if (isAllFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredList.map((item) => item.id)));
    }
  };

  // Toggle selection for all items belonging to a student group
  const handleToggleSelectGroup = (items: StudentExtracurricular[]) => {
    const next = new Set(selectedIds);
    const allSelected = items.every((item) => next.has(item.id));
    if (allSelected) {
      items.forEach((item) => next.delete(item.id));
    } else {
      items.forEach((item) => next.add(item.id));
    }
    setSelectedIds(next);
  };

  // Toggle selection for individual item
  const handleToggleSelectItem = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  // Delete selected items
  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (window.confirm(`Apakah Anda yakin ingin MENGHAPUS ${count} data ekstrakurikuler yang dipilih?`)) {
      setIsSaving(true);
      try {
        const targets = classExtracurriculars.filter((e) => selectedIds.has(e.id));
        for (const target of targets) {
          if (onSaveExtracurricular) {
            await onSaveExtracurricular(target, 'DELETE');
          }
        }
        setSelectedIds(new Set());
      } catch (err) {
        console.error(err);
        alert('Gagal menghapus beberapa data.');
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Delete ALL class data
  const handleDeleteAllClassData = async () => {
    if (classExtracurriculars.length === 0) {
      alert('Belum ada data ekstrakurikuler untuk kelas ini.');
      return;
    }
    const count = classExtracurriculars.length;
    if (
      window.confirm(
        `PERINGATAN SANGAT PENTING!\n\nApakah Anda benar-benar yakin ingin MENGHAPUS SEMUA (${count}) data nilai ekstrakurikuler kelas ${homeroomClass}?\n\nData yang dihapus tidak dapat dikembalikan.`
      )
    ) {
      setIsSaving(true);
      try {
        for (const target of classExtracurriculars) {
          if (onSaveExtracurricular) {
            await onSaveExtracurricular(target, 'DELETE');
          }
        }
        setSelectedIds(new Set());
      } catch (err) {
        console.error(err);
        alert('Gagal menghapus seluruh data ekstrakurikuler.');
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Batch Form Modal State ("Pengisian Cepat per Kelas")
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [batchExtraName, setBatchExtraName] = useState('Pramuka (Wajib)');
  const [batchCustomExtraName, setBatchCustomExtraName] = useState('');
  const [batchDefaultGrade, setBatchDefaultGrade] = useState<ExtracurricularGrade>('Sangat Baik');
  const [batchEntries, setBatchEntries] = useState<Array<{
    studentId: string;
    studentName: string;
    grade: ExtracurricularGrade;
    description: string;
    isSelected: boolean;
  }>>([]);

  // Statistics
  const stats = useMemo(() => {
    const totalRecords = classExtracurriculars.length;
    const participatingStudents = new Set(classExtracurriculars.map(e => e.studentId)).size;
    const sangatBaik = classExtracurriculars.filter(e => e.grade === 'Sangat Baik' || e.grade === 'A').length;
    const baik = classExtracurriculars.filter(e => e.grade === 'Baik' || e.grade === 'B').length;
    const cukup = classExtracurriculars.filter(e => e.grade === 'Cukup' || e.grade === 'C').length;
    return { totalRecords, participatingStudents, totalStudents: students.length, sangatBaik, baik, cukup };
  }, [classExtracurriculars, students.length]);

  // Open Modal for Add
  const handleOpenAddModal = (studentIdPreset?: string) => {
    setEditingItem(null);
    setFormStudentId(studentIdPreset || (sortedStudents[0]?.id || ''));
    setFormExtraName(PRESET_EXTRAS[0]);
    setFormCustomExtraName('');
    setFormGrade('Sangat Baik');
    setFormDescription(PRESET_DESCRIPTIONS['Sangat Baik'][0]);
    setIsModalOpen(true);
  };

  // Open Modal for Edit
  const handleOpenEditModal = (item: StudentExtracurricular) => {
    setEditingItem(item);
    setFormStudentId(item.studentId);
    
    if (PRESET_EXTRAS.includes(item.extraName)) {
      setFormExtraName(item.extraName);
      setFormCustomExtraName('');
    } else {
      setFormExtraName('LAINNYA');
      setFormCustomExtraName(item.extraName);
    }

    // Normalize grade display
    let g: ExtracurricularGrade = item.grade;
    if (g as string === 'A') g = 'Sangat Baik';
    if (g as string === 'B') g = 'Baik';
    if (g as string === 'C') g = 'Cukup';
    if (g as string === 'D') g = 'Kurang';

    setFormGrade(g);
    setFormDescription(item.description || '');
    setIsModalOpen(true);
  };

  // Handle Grade Change and auto-suggest description
  const handleGradeChange = (newGrade: ExtracurricularGrade) => {
    setFormGrade(newGrade);
    const key = newGrade === 'A' ? 'Sangat Baik' : newGrade === 'B' ? 'Baik' : newGrade === 'C' ? 'Cukup' : newGrade === 'D' ? 'Kurang' : newGrade;
    if (PRESET_DESCRIPTIONS[key] && PRESET_DESCRIPTIONS[key].length > 0) {
      setFormDescription(PRESET_DESCRIPTIONS[key][0]);
    }
  };

  // Save Single Item
  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formStudentId) {
      alert('Silakan pilih peserta didik terlebih dahulu.');
      return;
    }

    const selectedStudent = students.find(s => s.id === formStudentId);
    if (!selectedStudent) {
      alert('Data peserta didik tidak ditemukan.');
      return;
    }

    const activeExtraName = formExtraName === 'LAINNYA' ? formCustomExtraName.trim() : formExtraName;
    if (!activeExtraName) {
      alert('Silakan tuliskan nama ekstrakurikuler.');
      return;
    }

    setIsSaving(true);
    try {
      const payload: StudentExtracurricular = {
        id: editingItem ? editingItem.id : `extra_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        studentId: selectedStudent.id,
        studentName: selectedStudent.name,
        class: homeroomClass,
        extraName: activeExtraName,
        grade: formGrade,
        description: formDescription.trim(),
        semester: semester,
        academicYear: academicYear,
        createdAt: editingItem?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      if (onSaveExtracurricular) {
        await onSaveExtracurricular(payload, editingItem ? 'PUT' : 'POST');
      }

      setIsModalOpen(false);
    } catch (err) {
      console.error(err);
      alert('Gagal menyimpan data ekstrakurikuler.');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete Single Item
  const handleDeleteItem = async (id: string, name: string) => {
    if (window.confirm(`Apakah Anda yakin ingin menghapus nilai ekstrakurikuler untuk ${name}?`)) {
      if (onSaveExtracurricular) {
        const target = classExtracurriculars.find(e => e.id === id);
        if (target) {
          await onSaveExtracurricular(target, 'DELETE');
        }
      }
    }
  };

  // Open Batch Modal ("Pengisian Cepat per Kelas")
  const handleOpenBatchModal = () => {
    const extra = 'Pramuka (Wajib)';
    setBatchExtraName(extra);
    setBatchCustomExtraName('');
    setBatchDefaultGrade('Sangat Baik');

    const defaultDesc = PRESET_DESCRIPTIONS['Sangat Baik'][0];

    const mapped = sortedStudents.map(s => {
      // Check if student already has a record for this extra
      const existing = classExtracurriculars.find(e => e.studentId === s.id && e.extraName === extra);
      return {
        studentId: s.id,
        studentName: s.name,
        grade: existing ? existing.grade : ('Sangat Baik' as ExtracurricularGrade),
        description: existing ? (existing.description || defaultDesc) : defaultDesc,
        isSelected: true
      };
    });

    setBatchEntries(mapped);
    setIsBatchModalOpen(true);
  };

  // Apply default grade to all checked in batch
  const handleApplyBatchDefaultGrade = (newGrade: ExtracurricularGrade) => {
    setBatchDefaultGrade(newGrade);
    const key = newGrade === 'A' ? 'Sangat Baik' : newGrade === 'B' ? 'Baik' : newGrade === 'C' ? 'Cukup' : newGrade === 'D' ? 'Kurang' : newGrade;
    const desc = PRESET_DESCRIPTIONS[key]?.[0] || '';

    setBatchEntries(prev => prev.map(entry => {
      if (!entry.isSelected) return entry;
      return {
        ...entry,
        grade: newGrade,
        description: desc
      };
    }));
  };

  // Save Batch Entries
  const handleSaveBatchEntries = async () => {
    const selected = batchEntries.filter(b => b.isSelected);
    if (selected.length === 0) {
      alert('Pilih setidaknya satu siswa untuk disimpan.');
      return;
    }

    const activeExtra = batchExtraName === 'LAINNYA' ? batchCustomExtraName.trim() : batchExtraName;
    if (!activeExtra) {
      alert('Nama ekstrakurikuler wajib diisi.');
      return;
    }

    setIsSaving(true);
    try {
      for (const entry of selected) {
        const existing = classExtracurriculars.find(e => e.studentId === entry.studentId && e.extraName === activeExtra);
        const payload: StudentExtracurricular = {
          id: existing ? existing.id : `extra_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
          studentId: entry.studentId,
          studentName: entry.studentName,
          class: homeroomClass,
          extraName: activeExtra,
          grade: entry.grade,
          description: entry.description,
          semester,
          academicYear,
          createdAt: existing?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        if (onSaveExtracurricular) {
          await onSaveExtracurricular(payload, existing ? 'PUT' : 'POST');
        }
      }

      setIsBatchModalOpen(false);
    } catch (err) {
      console.error(err);
      alert('Terjadi kesalahan saat menyimpan data massal.');
    } finally {
      setIsSaving(false);
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (classExtracurriculars.length === 0) {
      alert('Belum ada data ekstrakurikuler untuk diexport.');
      return;
    }

    const schoolTitle = schoolSettings?.schoolName || 'SMP NEGERI 1';
    const headerData = [
      [schoolTitle.toUpperCase()],
      [`DAFTAR NILAI EKSTRAKURIKULER SISWA KELAS ${homeroomClass}`],
      [`Tahun Ajaran: ${academicYear} | Semester: ${semester}`],
      [`Wali Kelas: ${activeUser.name}`],
      [],
      ['No', 'NIS / ID', 'Nama Peserta Didik', 'Kelas', 'Kegiatan Ekstrakurikuler', 'Predikat / Nilai', 'Keterangan / Narasi Catatan']
    ];

    const rows = filteredList.map((item, idx) => {
      const studentObj = students.find(s => s.id === item.studentId);
      return [
        idx + 1,
        studentObj?.nis || item.studentId,
        item.studentName,
        item.class,
        item.extraName,
        item.grade,
        item.description || '-'
      ];
    });

    const ws = XLSX.utils.aoa_to_sheet([...headerData, ...rows]);
    ws['!cols'] = [
      { wch: 5 },
      { wch: 14 },
      { wch: 28 },
      { wch: 8 },
      { wch: 28 },
      { wch: 15 },
      { wch: 60 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Ekstrakurikuler_${homeroomClass}`);
    XLSX.writeFile(wb, `Extrakurikuler_Kelas_${homeroomClass}_${academicYear.replace('/', '-')}.xlsx`);
  };

  // Print Official Report
  const handlePrintReport = () => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    const schoolTitle = schoolSettings.schoolName || 'SMP NEGERI 1';
    const govTitle = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    const deptTitle = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    const address = schoolSettings.address || 'Alamat Sekolah';

    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Laporan Ekstrakurikuler Kelas ${homeroomClass} - ${schoolTitle}</title>
          <style>
            @page {
              size: A4;
              margin: 15mm;
            }
            body {
              font-family: 'Times New Roman', Times, serif;
              color: #000;
              line-height: 1.4;
              margin: 0;
              padding: 0;
            }
            .kop {
              text-align: center;
              border-bottom: 2.5px solid #000;
              padding-bottom: 8px;
              margin-bottom: 16px;
            }
            .kop h3 { margin: 0; font-size: 11pt; font-weight: normal; text-transform: uppercase; }
            .kop h2 { margin: 2px 0; font-size: 13pt; font-weight: bold; text-transform: uppercase; }
            .kop h1 { margin: 2px 0; font-size: 15pt; font-weight: bold; text-transform: uppercase; }
            .kop p { margin: 2px 0; font-size: 9pt; font-style: italic; }
            
            .doc-title {
              text-align: center;
              margin-bottom: 16px;
            }
            .doc-title h2 {
              font-size: 12pt;
              margin: 0;
              text-decoration: underline;
              text-transform: uppercase;
            }
            .doc-title p {
              margin: 3px 0 0 0;
              font-size: 10pt;
              font-weight: bold;
            }

            table.meta {
              width: 100%;
              margin-bottom: 12px;
              font-size: 10pt;
            }
            table.meta td { padding: 2px 0; }

            table.data {
              width: 100%;
              border-collapse: collapse;
              font-size: 9.5pt;
            }
            table.data th, table.data td {
              border: 1px solid #000;
              padding: 6px 8px;
              vertical-align: top;
            }
            table.data th {
              background-color: #f2f2f2;
              text-align: center;
              font-weight: bold;
            }
            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }

            .sig-container {
              margin-top: 30px;
              width: 100%;
              font-size: 10pt;
            }
            .sig-space { height: 60px; }
          </style>
        </head>
        <body>
          <div class="kop">
            <h3>${govTitle}</h3>
            <h3>${deptTitle}</h3>
            <h1>${schoolTitle}</h1>
            <p>${address}</p>
          </div>

          <div class="doc-title">
            <h2>DOKUMEN NILAI & NARASI EKSTRAKURIKULER</h2>
            <p>KELAS ${homeroomClass} • SEMESTER ${semester.toUpperCase()} TAHUN AJARAN ${academicYear}</p>
          </div>

          <table class="meta">
            <tr>
              <td width="15%"><strong>Satuan Pendidikan</strong></td>
              <td width="35%">: ${schoolTitle}</td>
              <td width="15%"><strong>Wali Kelas</strong></td>
              <td width="35%">: ${activeUser.name} (${activeUser.nip ? 'NIP. ' + activeUser.nip : '-'})</td>
            </tr>
            <tr>
              <td><strong>Kelas / Rombel</strong></td>
              <td>: ${homeroomClass}</td>
              <td><strong>Jumlah Siswa</strong></td>
              <td>: ${students.length} Peserta Didik (${classExtracurriculars.length} Nilai Terdata)</td>
            </tr>
          </table>

          <table class="data">
            <thead>
              <tr>
                <th width="4%">NO</th>
                <th width="24%">NAMA PESERTA DIDIK</th>
                <th width="22%">KEGIATAN EKSTRAKURIKULER</th>
                <th width="12%">PREDIKAT</th>
                <th width="38%">KETERANGAN / NARASI CATATAN</th>
              </tr>
            </thead>
            <tbody>
              ${groupedList.length === 0 ? `
                <tr>
                  <td colspan="5" class="text-center" style="padding: 20px;">Belum ada data nilai ekstrakurikuler yang dimasukkan.</td>
                </tr>
              ` : groupedList.map((grp, idx) => `
                ${grp.items.map((item, itemIdx) => `
                  <tr>
                    ${itemIdx === 0 ? `
                      <td class="text-center" rowspan="${grp.items.length}">${idx + 1}</td>
                      <td rowspan="${grp.items.length}"><strong>${grp.studentName}</strong></td>
                    ` : ''}
                    <td><strong>${item.extraName}</strong></td>
                    <td class="text-center font-bold">${item.grade}</td>
                    <td>${item.description || '-'}</td>
                  </tr>
                `).join('')}
              `).join('')}
            </tbody>
          </table>

          <table class="sig-container">
            <tr>
              <td width="50%" align="center">
                Mengetahui,<br/>
                Kepala Sekolah
                <div class="sig-space"></div>
                <strong>${schoolSettings.principalName || '................................'}</strong><br/>
                NIP. ${schoolSettings.principalNip || '................................'}
              </td>
              <td width="50%" align="center">
                Sumedang, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
                Wali Kelas ${homeroomClass}
                <div class="sig-space"></div>
                <strong>${activeUser.name}</strong><br/>
                NIP. ${activeUser.nip || '................................'}
              </td>
            </tr>
          </table>

          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `;

    printWin.document.write(content);
    printWin.document.close();
  };

  return (
    <div className="space-y-6">
      {/* Overview Banner Card */}
      <div className="bg-gradient-to-br from-emerald-900 via-teal-800 to-indigo-900 rounded-3xl p-6 text-white shadow-xl border border-emerald-700/50 relative overflow-hidden">
        <div className="absolute -right-8 -bottom-8 w-48 h-48 bg-emerald-500/10 rounded-full blur-2xl" />
        
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-bold border border-white/20 text-emerald-200">
              <Award size={14} className="text-amber-300" />
              <span>Administrasi Ekstrakurikuler Rapor</span>
            </div>
            <h2 className="text-xl md:text-2xl font-black tracking-tight">
              Form & Nilai Ekstrakurikuler Kelas {homeroomClass}
            </h2>
            <p className="text-xs md:text-sm text-emerald-100/90 max-w-xl">
              Pengisian nilai predikat dan deskripsi perkembangan kegiatan ekstrakurikuler peserta didik kelas perwalian Anda untuk pencetakan rapor.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {selectedIds.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white text-xs md:text-sm font-black rounded-xl shadow-lg transition-all transform hover:scale-102 cursor-pointer animate-in fade-in"
              >
                <Trash2 size={16} />
                <span>Hapus Terpilih ({selectedIds.size})</span>
              </button>
            )}

            <button
              onClick={handleDeleteAllClassData}
              disabled={classExtracurriculars.length === 0 || isSaving}
              className="flex items-center gap-2 px-3.5 py-2.5 bg-rose-600/30 hover:bg-rose-600/60 disabled:opacity-40 text-white text-xs md:text-sm font-black rounded-xl border border-rose-400/30 transition-all cursor-pointer disabled:cursor-not-allowed"
              title={`Hapus Seluruh Data Nilai Ekstrakurikuler Kelas ${homeroomClass}`}
            >
              <Trash2 size={16} />
              <span>Hapus Semua</span>
            </button>

            <button
              onClick={handleOpenBatchModal}
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-400 hover:bg-amber-300 text-slate-900 text-xs md:text-sm font-black rounded-xl shadow-lg transition-all transform hover:scale-102 cursor-pointer"
            >
              <Zap size={16} />
              <span>Pengisian Massal Kelas</span>
            </button>

            <button
              onClick={() => handleOpenAddModal()}
              className="flex items-center gap-2 px-4 py-2.5 bg-white text-emerald-950 hover:bg-emerald-50 text-xs md:text-sm font-extrabold rounded-xl shadow-lg transition-all cursor-pointer"
            >
              <Plus size={16} />
              <span>Tambah Nilai</span>
            </button>

            <button
              onClick={handlePrintReport}
              disabled={classExtracurriculars.length === 0}
              className="flex items-center gap-1.5 px-3.5 py-2.5 bg-black/30 hover:bg-black/50 disabled:opacity-50 text-white text-xs font-bold rounded-xl border border-white/20 transition-all cursor-pointer disabled:cursor-not-allowed"
            >
              <Printer size={15} />
              <span>Cetak Laporan</span>
            </button>

            <button
              onClick={handleExportExcel}
              disabled={classExtracurriculars.length === 0}
              className="flex items-center gap-1.5 px-3.5 py-2.5 bg-black/30 hover:bg-black/50 disabled:opacity-50 text-white text-xs font-bold rounded-xl border border-white/20 transition-all cursor-pointer disabled:cursor-not-allowed"
            >
              <Download size={15} />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-5 border-t border-white/10">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/10">
            <span className="text-[11px] text-emerald-200 font-semibold block">Total Catatan Nilai</span>
            <span className="text-xl font-black mt-0.5 block">{stats.totalRecords} Entries</span>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/10">
            <span className="text-[11px] text-emerald-200 font-semibold block">Siswa Ber-Ekskul</span>
            <span className="text-xl font-black mt-0.5 block">{stats.participatingStudents} / {stats.totalStudents} Siswa</span>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/10">
            <span className="text-[11px] text-emerald-200 font-semibold block">Predikat Sangat Baik (A)</span>
            <span className="text-xl font-black text-amber-300 mt-0.5 block">{stats.sangatBaik} Siswa</span>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/10">
            <span className="text-[11px] text-emerald-200 font-semibold block">Predikat Baik (B)</span>
            <span className="text-xl font-black text-emerald-300 mt-0.5 block">{stats.baik} Siswa</span>
          </div>
        </div>
      </div>

      {/* Control Filters Bar */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="relative w-full md:w-72">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Cari nama siswa / ekskul..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm outline-none focus:ring-2 focus:ring-emerald-500 transition-all text-slate-800 dark:text-white"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
          {/* Filter Extra Name */}
          <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-1.5 rounded-xl">
            <Filter size={13} className="text-slate-400" />
            <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">Ekskul:</span>
            <select
              value={extraFilter}
              onChange={(e) => setExtraFilter(e.target.value)}
              className="bg-transparent text-xs font-bold text-slate-800 dark:text-white outline-none cursor-pointer"
            >
              <option value="SEMUA">Semua Ekskul</option>
              {uniqueExtraNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          {/* Filter Grade */}
          <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-1.5 rounded-xl">
            <Award size={13} className="text-slate-400" />
            <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">Predikat:</span>
            <select
              value={gradeFilter}
              onChange={(e) => setGradeFilter(e.target.value)}
              className="bg-transparent text-xs font-bold text-slate-800 dark:text-white outline-none cursor-pointer"
            >
              <option value="SEMUA">Semua Predikat</option>
              <option value="Sangat Baik">Sangat Baik (A)</option>
              <option value="Baik">Baik (B)</option>
              <option value="Cukup">Cukup (C)</option>
              <option value="Kurang">Kurang (D)</option>
            </select>
          </div>

          {/* Sort Order Toggle */}
          <button
            type="button"
            onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
            className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-900 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 border border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-700 px-3 py-1.5 rounded-xl transition-colors cursor-pointer text-slate-700 dark:text-slate-200 text-xs font-bold"
            title="Klik untuk mengubah urutan alfabet nama siswa"
          >
            <ArrowUpDown size={13} className="text-emerald-600 dark:text-emerald-400" />
            <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">Urut Nama:</span>
            <span className="text-emerald-700 dark:text-emerald-300 font-extrabold text-[11px]">
              {sortDirection === 'asc' ? 'Alfabet (A - Z)' : 'Alfabet (Z - A)'}
            </span>
          </button>
        </div>
      </div>

      {/* Selection Action Bar (when checkboxes are selected) */}
      {selectedIds.size > 0 && (
        <div className="bg-rose-50 dark:bg-rose-950/80 border border-rose-200 dark:border-rose-800/80 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-sm animate-in fade-in">
          <div className="flex items-center gap-2.5 text-rose-900 dark:text-rose-200 font-bold text-xs sm:text-sm">
            <CheckCircle2 size={18} className="text-rose-600 dark:text-rose-400 shrink-0" />
            <span>
              <strong className="font-black">{selectedIds.size}</strong> dari {filteredList.length} data ekstrakurikuler terpilih
            </span>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3.5 py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-100 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 transition-colors cursor-pointer"
            >
              Batal Pilih
            </button>
            <button
              onClick={handleDeleteSelected}
              disabled={isSaving}
              className="flex items-center justify-center gap-1.5 px-4 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-sm transition-all cursor-pointer"
            >
              <Trash2 size={14} />
              <span>Hapus {selectedIds.size} Data Terpilih</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Table View */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        {filteredList.length === 0 ? (
          <div className="p-12 text-center space-y-4">
            <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto">
              <Award size={32} />
            </div>
            <div className="space-y-1">
              <h3 className="font-bold text-slate-800 dark:text-white text-base">
                {classExtracurriculars.length === 0 ? 'Belum Ada Nilai Ekstrakurikuler' : 'Tidak Ada Data Ditemukan'}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                {classExtracurriculars.length === 0
                  ? `Silakan tambahkan data ekstrakurikuler untuk peserta didik kelas ${homeroomClass} atau gunakan tombol Pengisian Massal.`
                  : 'Coba ubah kata kunci pencarian atau filter predikat/ekstrakurikuler di atas.'}
              </p>
            </div>
            {classExtracurriculars.length === 0 && (
              <div className="flex justify-center gap-3 pt-2">
                <button
                  onClick={handleOpenBatchModal}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
                >
                  <Zap size={15} />
                  <span>Isi Massal Kelas</span>
                </button>
                <button
                  onClick={() => handleOpenAddModal()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
                >
                  <Plus size={15} />
                  <span>Tambah Manual</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-700 text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  <th className="py-3.5 px-4 text-center w-10">
                    <input
                      type="checkbox"
                      checked={isAllFilteredSelected}
                      onChange={handleToggleSelectAll}
                      className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
                      title="Pilih Semua Data Ekskul"
                    />
                  </th>
                  <th className="py-3.5 px-3 text-center w-12">NO</th>
                  <th 
                    className="py-3.5 px-4 w-60 sm:w-72 cursor-pointer select-none group"
                    onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                    title="Klik untuk mengubah urutan nama (A-Z / Z-A)"
                  >
                    <div className="flex items-center gap-1.5 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                      <span>NAMA PESERTA DIDIK</span>
                      <ArrowUpDown size={12} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                      <span className="text-[10px] font-black px-1.5 py-0.2 rounded bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300">
                        {sortDirection === 'asc' ? 'A - Z' : 'Z - A'}
                      </span>
                    </div>
                  </th>
                  <th className="py-3.5 px-4">DAFTAR KEGIATAN EKSTRAKURIKULER & CATATAN RAPOR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60 text-xs">
                {groupedList.map((group, idx) => {
                  const studentObj = students.find((s) => s.id === group.studentId);
                  const isGroupAllSelected = group.items.length > 0 && group.items.every((item) => selectedIds.has(item.id));

                  return (
                    <tr key={group.studentId} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors">
                      <td className="py-4 px-4 text-center align-top pt-5">
                        <input
                          type="checkbox"
                          checked={isGroupAllSelected}
                          onChange={() => handleToggleSelectGroup(group.items)}
                          className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
                          title={`Pilih Semua Kegiatan Ekskul ${group.studentName}`}
                        />
                      </td>

                      <td className="py-4 px-3 text-center font-bold text-slate-400 align-top pt-5">
                        {idx + 1}
                      </td>

                      <td className="py-4 px-4 align-top font-bold text-slate-800 dark:text-white">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300 flex items-center justify-center font-black text-xs shrink-0 shadow-2xs">
                              {group.studentName.charAt(0)}
                            </div>
                            <div>
                              <div className="font-extrabold text-sm text-slate-900 dark:text-white leading-snug">
                                {group.studentName}
                              </div>
                              <div className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold">
                                NIS: {studentObj?.nis || '-'}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2 pt-1">
                            <span className="px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 font-bold text-[10px] border border-emerald-200/80 dark:border-emerald-800">
                              {group.items.length} Ekskul
                            </span>

                            <button
                              onClick={() => handleOpenAddModal(group.studentId)}
                              className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                              title="Tambah kegiatan ekskul lain untuk siswa ini"
                            >
                              <Plus size={12} />
                              <span>+ Tambah Ekskul</span>
                            </button>
                          </div>
                        </div>
                      </td>

                      <td className="py-4 px-4 align-top">
                        <div className="space-y-2.5">
                          {group.items.map((item) => {
                            const isSangatBaik = item.grade === 'Sangat Baik' || item.grade === 'A';
                            const isBaik = item.grade === 'Baik' || item.grade === 'B';
                            const isCukup = item.grade === 'Cukup' || item.grade === 'C';
                            const isItemSelected = selectedIds.has(item.id);

                            return (
                              <div
                                key={item.id}
                                className={`p-3 rounded-xl border flex flex-col sm:flex-row sm:items-start justify-between gap-3 group/item transition-all ${
                                  isItemSelected
                                    ? 'bg-emerald-50/80 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-700 shadow-xs'
                                    : 'bg-slate-50 dark:bg-slate-900/60 border-slate-200/80 dark:border-slate-700/80 hover:border-emerald-300 dark:hover:border-emerald-800'
                                }`}
                              >
                                <div className="space-y-1.5 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={isItemSelected}
                                      onChange={() => handleToggleSelectItem(item.id)}
                                      className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer shrink-0"
                                      title="Pilih data kegiatan ini"
                                    />

                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-indigo-100 dark:bg-indigo-950 text-indigo-800 dark:text-indigo-200 rounded-lg font-black text-xs border border-indigo-200 dark:border-indigo-800">
                                      <BookOpen size={13} />
                                      <span>{item.extraName}</span>
                                    </span>

                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-black ${
                                      isSangatBaik
                                        ? 'bg-amber-100 text-amber-900 border border-amber-300 dark:bg-amber-950 dark:text-amber-200'
                                        : isBaik
                                        ? 'bg-emerald-100 text-emerald-900 border border-emerald-300 dark:bg-emerald-950 dark:text-emerald-200'
                                        : isCukup
                                        ? 'bg-blue-100 text-blue-900 border border-blue-300 dark:bg-blue-950 dark:text-blue-200'
                                        : 'bg-rose-100 text-rose-900 border border-rose-300 dark:bg-rose-950 dark:text-rose-200'
                                    }`}>
                                      Predikat: {item.grade}
                                    </span>
                                  </div>

                                  <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed pt-0.5 pl-6 sm:pl-0">
                                    {item.description || <span className="italic text-slate-400">Tidak ada keterangan</span>}
                                  </p>
                                </div>

                                <div className="flex items-center gap-1 shrink-0 pt-1 sm:pt-0">
                                  <button
                                    onClick={() => handleOpenEditModal(item)}
                                    title="Edit Kegiatan Ini"
                                    className="p-1.5 text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-white dark:hover:bg-slate-800 rounded-lg border border-transparent hover:border-slate-200 dark:hover:border-slate-700 transition-colors cursor-pointer"
                                  >
                                    <Edit3 size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteItem(item.id, item.studentName)}
                                    title="Hapus Kegiatan Ini"
                                    className="p-1.5 text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-white dark:hover:bg-slate-800 rounded-lg border border-transparent hover:border-slate-200 dark:hover:border-slate-700 transition-colors cursor-pointer"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* SINGLE FORM MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-xl w-full border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[92vh] flex flex-col my-auto">
            {/* Modal Header */}
            <div className="p-5 bg-gradient-to-r from-emerald-800 to-teal-800 text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-xl">
                  <Award size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-base">
                    {editingItem ? 'Edit Nilai Ekstrakurikuler' : 'Tambah Nilai Ekstrakurikuler'}
                  </h3>
                  <p className="text-xs text-emerald-200">
                    Kelas {homeroomClass} • Semester {semester}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-xl hover:bg-white/10 text-emerald-100 hover:text-white transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Form Container */}
            <form onSubmit={handleSaveItem} className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4">
              {/* Select Student */}
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  Pilih Peserta Didik <span className="text-rose-500">*</span>
                </label>
                <select
                  value={formStudentId}
                  disabled={!!editingItem}
                  onChange={(e) => setFormStudentId(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white text-xs sm:text-sm font-bold outline-none focus:ring-2 focus:ring-emerald-500 transition-all disabled:opacity-75 cursor-pointer"
                >
                  <option value="">-- Pilih Siswa --</option>
                  {sortedStudents.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} (NIS: {s.nis || '-'})
                    </option>
                  ))}
                </select>
              </div>

              {/* Select Extracurricular */}
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  Kegiatan Ekstrakurikuler <span className="text-rose-500">*</span>
                </label>
                <select
                  value={formExtraName}
                  onChange={(e) => setFormExtraName(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white text-xs sm:text-sm font-bold outline-none focus:ring-2 focus:ring-emerald-500 transition-all cursor-pointer"
                >
                  {PRESET_EXTRAS.map((extra) => (
                    <option key={extra} value={extra}>
                      {extra}
                    </option>
                  ))}
                  <option value="LAINNYA">+ Ketik Manual (Lainnya)...</option>
                </select>

                {formExtraName === 'LAINNYA' && (
                  <input
                    type="text"
                    placeholder="Tuliskan nama ekstrakurikuler..."
                    value={formCustomExtraName}
                    onChange={(e) => setFormCustomExtraName(e.target.value)}
                    className="mt-2 w-full px-3.5 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-white text-xs sm:text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                )}
              </div>

              {/* Predikat / Nilai */}
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  Predikat / Nilai <span className="text-rose-500">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(['Sangat Baik', 'Baik', 'Cukup', 'Kurang'] as ExtracurricularGrade[]).map((g) => {
                    const isSelected = formGrade === g;
                    return (
                      <button
                        type="button"
                        key={g}
                        onClick={() => handleGradeChange(g)}
                        className={`py-2.5 px-3 rounded-xl border text-xs font-black transition-all cursor-pointer text-center ${
                          isSelected
                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-md scale-102'
                            : 'bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-emerald-400'
                        }`}
                      >
                        {g}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Description / Narasi Rapor */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    Deskripsi / Catatan Perkembangan (Rapor)
                  </label>
                  <span className="text-[10px] text-slate-400 font-semibold">Tersedia Rekomendasi</span>
                </div>

                <textarea
                  rows={3}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Tuliskan catatan narasi ekstrakurikuler siswa..."
                  className="w-full p-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white text-xs sm:text-sm outline-none focus:ring-2 focus:ring-emerald-500 transition-all leading-relaxed"
                />

                {/* Preset Suggestions */}
                <div className="mt-2 space-y-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Pilihan Narasi Otomatis ({formGrade}):
                  </span>
                  <div className="flex flex-col gap-1">
                    {(PRESET_DESCRIPTIONS[formGrade] || []).map((preset, idx) => (
                      <button
                        type="button"
                        key={idx}
                        onClick={() => setFormDescription(preset)}
                        className="text-left text-[11px] p-2 rounded-lg bg-slate-100 dark:bg-slate-900 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 hover:text-emerald-700 dark:hover:text-emerald-300 border border-slate-200/80 dark:border-slate-800 text-slate-600 dark:text-slate-300 transition-colors line-clamp-2 cursor-pointer"
                      >
                        ⚡ "{preset}"
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Footer */}
              <div className="pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-bold text-xs hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-extrabold text-xs shadow-md transition-all cursor-pointer"
                >
                  <Check size={16} />
                  <span>{isSaving ? 'Menyimpan...' : 'Simpan Data'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BATCH FORM MODAL ("Pengisian Cepat per Kelas") */}
      {isBatchModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-4xl w-full border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-5 bg-gradient-to-r from-amber-600 via-emerald-700 to-teal-800 text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-xl">
                  <Zap size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-base">Pengisian Nilai Ekstrakurikuler Massal</h3>
                  <p className="text-xs text-amber-100">
                    Isi nilai ekstrakurikuler sekaligus untuk seluruh siswa kelas {homeroomClass}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsBatchModalOpen(false)}
                className="p-1.5 rounded-xl hover:bg-white/10 text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Batch Form Settings */}
            <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 space-y-3 shrink-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Select Extra Name */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1">
                    Kegiatan Ekstrakurikuler
                  </label>
                  <select
                    value={batchExtraName}
                    onChange={(e) => setBatchExtraName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-white text-xs font-bold outline-none cursor-pointer"
                  >
                    {PRESET_EXTRAS.map(extra => (
                      <option key={extra} value={extra}>{extra}</option>
                    ))}
                    <option value="LAINNYA">+ Ketik Manual (Lainnya)...</option>
                  </select>

                  {batchExtraName === 'LAINNYA' && (
                    <input
                      type="text"
                      placeholder="Nama Ekstrakurikuler..."
                      value={batchCustomExtraName}
                      onChange={(e) => setBatchCustomExtraName(e.target.value)}
                      className="mt-1.5 w-full px-3 py-1.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-800 dark:text-white outline-none"
                    />
                  )}
                </div>

                {/* Default Predikat Quick Setter */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1">
                    Set Predikat Default untuk Semua:
                  </label>
                  <div className="flex items-center gap-1.5">
                    {(['Sangat Baik', 'Baik', 'Cukup'] as ExtracurricularGrade[]).map(g => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => handleApplyBatchDefaultGrade(g)}
                        className={`flex-1 py-1.5 px-2 rounded-xl text-xs font-black border transition-all cursor-pointer text-center ${
                          batchDefaultGrade === g
                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                            : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700'
                        }`}
                      >
                        Set All {g}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Batch Table List */}
            <div className="p-4 overflow-y-auto flex-1">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-400 font-bold uppercase text-[10px]">
                    <th className="py-2 px-2 text-center w-10">
                      <input
                        type="checkbox"
                        checked={batchEntries.every(b => b.isSelected)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setBatchEntries(prev => prev.map(p => ({ ...p, isSelected: checked })));
                        }}
                        className="rounded cursor-pointer"
                      />
                    </th>
                    <th className="py-2 px-3">Nama Siswa</th>
                    <th className="py-2 px-3 w-40">Predikat</th>
                    <th className="py-2 px-3">Deskripsi / Catatan Rapor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                  {batchEntries.map((entry, idx) => (
                    <tr key={entry.studentId} className={entry.isSelected ? 'bg-white dark:bg-slate-800' : 'bg-slate-50/50 dark:bg-slate-900/50 opacity-60'}>
                      <td className="py-2.5 px-2 text-center">
                        <input
                          type="checkbox"
                          checked={entry.isSelected}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setBatchEntries(prev => prev.map((p, i) => i === idx ? { ...p, isSelected: checked } : p));
                          }}
                          className="rounded cursor-pointer"
                        />
                      </td>

                      <td className="py-2.5 px-3 font-bold text-slate-800 dark:text-white">
                        {entry.studentName}
                      </td>

                      <td className="py-2.5 px-3">
                        <select
                          value={entry.grade}
                          onChange={(e) => {
                            const g = e.target.value as ExtracurricularGrade;
                            const key = g === 'A' ? 'Sangat Baik' : g === 'B' ? 'Baik' : g === 'C' ? 'Cukup' : g;
                            const desc = PRESET_DESCRIPTIONS[key]?.[0] || entry.description;
                            setBatchEntries(prev => prev.map((p, i) => i === idx ? { ...p, grade: g, description: desc } : p));
                          }}
                          className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white font-bold outline-none cursor-pointer"
                        >
                          <option value="Sangat Baik">Sangat Baik (A)</option>
                          <option value="Baik">Baik (B)</option>
                          <option value="Cukup">Cukup (C)</option>
                          <option value="Kurang">Kurang (D)</option>
                        </select>
                      </td>

                      <td className="py-2.5 px-3">
                        <input
                          type="text"
                          value={entry.description}
                          onChange={(e) => {
                            const val = e.target.value;
                            setBatchEntries(prev => prev.map((p, i) => i === idx ? { ...p, description: val } : p));
                          }}
                          className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-white text-xs outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex items-center justify-between shrink-0">
              <span className="text-xs text-slate-500 font-semibold">
                Terpilih {batchEntries.filter(b => b.isSelected).length} dari {batchEntries.length} siswa
              </span>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsBatchModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-bold text-xs hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="button"
                  onClick={handleSaveBatchEntries}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-black text-xs shadow-md transition-all cursor-pointer"
                >
                  <Check size={16} />
                  <span>{isSaving ? 'Menyimpan...' : 'Simpan Semua Siswa'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtracurricularManager;
